import { useEffect, useState } from "react";
import { MessageSquare, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    const day = d.toLocaleDateString("pt-PT", { day: "numeric", month: "short" });
    const time = d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
    return `${day}, ${time}`;
  } catch { return dateStr; }
}

export default function BlogComments({ postId }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", content: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadComments();
  }, [postId]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.content.trim()) return;
    setSending(true);
    try {
      const res = await fetch("/api/blog-comment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ post_id: postId, name: form.name.trim(), email: form.email.trim(), content: form.content.trim() }),
      });
      if (!res.ok) throw new Error();
      toast.success("Comentário enviado.");
      setForm({ name: "", email: "", content: "" });
      loadComments();
    } catch {
      toast.error("Erro ao enviar comentário.");
    } finally {
      setSending(false);
    }
  }

  function loadComments() {
    setLoading(true);
    fetch(`/api/blog-comments?post_id=${postId}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setComments(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  return (
    <div data-id="blog-comments">
      <p className="text-[10px] tracking-[0.28em] uppercase font-medium mb-8" style={{ color: "#C8A97E" }}>
        COMENTÁRIOS {comments.length > 0 && `(${comments.length})`}
      </p>

      {/* Existing comments */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 size={16} className="animate-spin" style={{ color: "#C8A97E" }} />
        </div>
      ) : comments.length > 0 ? (
        <div className="space-y-6 mb-12">
          {comments.map((c) => (
            <div key={c.uid} className="flex gap-4">
              <div className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-[10px] font-medium uppercase" style={{ backgroundColor: "rgba(200,169,126,0.12)", color: "#C8A97E" }}>
                {(c.name || "A")[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium" style={{ color: "#2B241F" }}>{c.name}</span>
                  <span className="text-[10px]" style={{ color: "#8C8178" }}>{formatDate(c.created_at)}</span>
                </div>
                <p className="text-sm leading-relaxed font-light mt-1.5" style={{ color: "#5A5148" }}>{c.content}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm font-light mb-10" style={{ color: "#8C8178" }}>
          Nenhum comentário ainda. Seja o primeiro.
        </p>
      )}

      {/* Comment form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-[10px] tracking-[0.2em] uppercase font-medium mb-3" style={{ color: "#8C8178" }}>DEIXAR UM COMENTÁRIO</p>

          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[9px] uppercase tracking-wider font-medium" style={{ color: "#8C8178" }}>Nome *</span>
              <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} className="w-full mt-1 px-3 py-2.5 text-sm border rounded-sm bg-transparent focus:outline-none focus:ring-0 transition-colors" style={{ borderColor: "#D8C7B5", color: "#2B241F" }} placeholder="O seu nome" />
            </label>
            <label className="block">
              <span className="text-[9px] uppercase tracking-wider font-medium" style={{ color: "#8C8178" }}>Email</span>
              <input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} className="w-full mt-1 px-3 py-2.5 text-sm border rounded-sm bg-transparent focus:outline-none focus:ring-0 transition-colors" style={{ borderColor: "#D8C7B5", color: "#2B241F" }} placeholder="email@exemplo.com" />
            </label>
          </div>

          <label className="block">
            <span className="text-[9px] uppercase tracking-wider font-medium" style={{ color: "#8C8178" }}>Comentário *</span>
            <textarea value={form.content} onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))} rows={4} className="w-full mt-1 px-3 py-2.5 text-sm border rounded-sm bg-transparent focus:outline-none focus:ring-0 transition-colors resize-none" style={{ borderColor: "#D8C7B5", color: "#2B241F" }} placeholder="Escreva o seu comentário..." />
          </label>

          <button type="submit" disabled={sending || !form.name.trim() || !form.content.trim()} className="inline-flex items-center gap-2 px-6 py-2.5 text-[10px] tracking-[0.2em] uppercase rounded-sm transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed" style={{ backgroundColor: "#2B241F", color: "#FAF8F5", border: "none" }}>
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            {sending ? "A enviar..." : "Enviar comentário"}
          </button>
        </form>
    </div>
  );
}
